import { environment } from 'src/environments/environment';

/**
 * Shared formatting utility functions
 */

/**
 * Format a US phone number for display
 * Converts +18337371332 to (833) 737-1332
 *
 * @param phone - Phone number in any format (with or without country code)
 * @returns Formatted phone number string
 */
export function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');

  // Handle US numbers with country code (11 digits starting with 1)
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    const areaCode = cleaned.substring(1, 4);
    const exchange = cleaned.substring(4, 7);
    const subscriber = cleaned.substring(7, 11);
    return `(${areaCode}) ${exchange}-${subscriber}`;
  }

  // Handle US numbers without country code (10 digits)
  if (cleaned.length === 10) {
    const areaCode = cleaned.substring(0, 3);
    const exchange = cleaned.substring(3, 6);
    const subscriber = cleaned.substring(6, 10);
    return `(${areaCode}) ${exchange}-${subscriber}`;
  }

  // Return original if format not recognized
  return phone;
}

/**
 * Reduce a stored US phone number to its 10 national digits.
 *
 * Feeding an 11-digit value straight into a 10-digit mask drops the *last*
 * digit, so "+1-555-123-4567" would silently become "(155) 512-3456" and
 * re-save corrupted. Use this wherever a stored phone is loaded into a
 * masked input.
 *
 * Handles the two US shapes only — 10 digits, and 11 digits with a leading 1.
 * Anything else (an international number, or a value carrying an extension) is
 * returned as-is and WILL be truncated by a 10-digit mask. That is deliberate:
 * a 10-digit mask cannot represent such a value, and slicing here would move
 * the same silent corruption into this function instead of surfacing it. ADN is
 * US-only, so this is a theoretical case; if it stops being one, these inputs
 * need a real international phone control, not a wider truncation rule.
 */
export function toNationalDigits(phone: string | null | undefined): string {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return cleaned.substring(1);
  }
  return cleaned;
}

/**
 * Replace the S3 domain in a signed URL with the static CDN domain.
 * Falls back to the original URL if parsing fails.
 */
export function toCdnUrl(signedUrl: string): string {
  try {
    const url = new URL(signedUrl);
    const cdnBase = environment.appCDN.endsWith('/')
      ? environment.appCDN.slice(0, -1)
      : environment.appCDN;
    return `${cdnBase}${url.pathname}${url.search}${url.hash}`;
  } catch {
    return signedUrl;
  }
}
